import React, { useState } from 'react';
import { Form, Button, Card, Row, Col } from 'react-bootstrap';
import Select from 'react-select';

const UserEdit = ({ organization_id, departments, user, onUpdateUser, onCancel, isAdmin, isOrgAdmin }) => {

  const [errors, setErrors] = useState({});
  const [editedUser, setEditedUser] = useState({
    ...user,
    roles: user.roles || [user.role], // Convert single role to array if needed
    firstname: user.firstname || '',
    lastname: user.lastname || '',
    phone: user.phone || '',
    organization_id: organization_id || '',
    is_enterprise: user.is_enterprise || false
  });

  const org = localStorage.getItem('org');

  const roleOptions = isAdmin ? [
    { value: 'user', label: 'User' },
    { value: 'admin', label: 'System Admin' },
    { value: 'org_admin', label: 'Enterprise Admin' }
  ] : isOrgAdmin ? [
    { value: 'user', label: 'User' },
    { value: 'org_admin', label: 'Enterprise Admin' }
  ] : [
    { value: 'user', label: 'User' }
  ];

  // Validation patterns
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const phonePattern = /^(\+1|1)?[-.\s]?\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
  const namePattern = /^[a-zA-Z\s\-]+$/;

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : null,
      '&:hover': {
        borderColor: state.isFocused ? '#86b7fe' : '#ced4da'
      }
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#e9ecef'
    })
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'firstname':
      case 'lastname':
        return value ? (namePattern.test(value) ? '' : 'No special characters allowed except hyphen') : 'This field is required';
      case 'email':
        return value ? (emailPattern.test(value) ? '' : 'Invalid email format') : 'Email is required';
      case 'phone':
        return value ? (phonePattern.test(value) ? '' : 'Invalid phone number format') : 'Phone number is required';
      case 'username':
        return value ? '' : 'Username is required';
      case 'roles':
        return value?.length > 0 ? '' : 'At least one role is required';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setEditedUser(prev => ({ ...prev, [name]: newValue }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, newValue) }));
  };

  const handleRolesChange = (selectedOptions) => {
    const roles = selectedOptions.map(option => option.value);
    setEditedUser(prev => ({ ...prev, roles }));
    setErrors(prev => ({ ...prev, roles: validateField('roles', roles) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields
    const newErrors = {};
    Object.keys(editedUser).forEach(key => {
      const error = validateField(key, editedUser[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onUpdateUser(editedUser);
  };

  return (
    <Card>
      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Form.Group as={Col} lg="6" className="mb-3">
              <Form.Label>First Name*</Form.Label>
              <Form.Control
                type="text"
                name="firstname"
                value={editedUser.firstname}
                onChange={handleChange}
                isInvalid={!!errors.firstname}
                placeholder="First Name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstname}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} lg="6" className="mb-3">
              <Form.Label>Last Name*</Form.Label>
              <Form.Control
                type="text"
                name="lastname"
                value={editedUser.lastname}
                onChange={handleChange}
                isInvalid={!!errors.lastname}
                placeholder="Last Name"
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastname}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row>
            <Form.Group as={Col} lg="6" className="mb-3">
              <Form.Label>Email*</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={editedUser.email}
                onChange={handleChange}
                isInvalid={!!errors.email}
                placeholder="Email@domain.com"
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} lg="6" className="mb-3">
              <Form.Label>Phone*</Form.Label>
              <Form.Control
                type="tel"
                name="phone"
                value={editedUser.phone}
                onChange={handleChange}
                isInvalid={!!errors.phone}
                placeholder="(123) 456-7890"
              />
              <Form.Control.Feedback type="invalid">
                {errors.phone}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="mb-3" as={Col} lg="6">
              <Form.Label>Username*</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={editedUser.username}
                onChange={handleChange}
                isInvalid={!!errors.username}
                placeholder="Username"
              />
              <Form.Control.Feedback type="invalid">
                {errors.username}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" as={Col} lg="6">
              <Form.Label>Department</Form.Label>
              <Form.Select
                name="department"
                value={editedUser.department}
                onChange={handleChange}
                style={{ fontSize: '0.8rem' }}
              >
                <option value="" style={{ fontSize: '0.8rem' }}>None</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.id} style={{ fontSize: '0.8rem' }}>
                    {dept.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Roles*</Form.Label>
            <Select
              isMulti
              name="roles"
              options={roleOptions}
              value={roleOptions.filter(option =>
                editedUser.roles?.includes(option.value)
              )}
              onChange={handleRolesChange}
              styles={selectStyles}
              placeholder="Select roles..."
              isInvalid={!!errors.roles}
            />
            {errors.roles && (
              <div className="text-danger small mt-1">{errors.roles}</div>
            )}
            <Form.Text className="text-muted">
              You can select multiple roles
            </Form.Text>
          </Form.Group>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  name="is_active"
                  checked={editedUser.is_active || false}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

          </Row>
          {/* Only show roles and checkboxes for admin users */}
          {isAdmin && org == "" && (
            <>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Paid"
                      name="is_paid"
                      checked={editedUser.is_paid || false}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Trial Expired"
                      name="trial_expired"
                      checked={editedUser.trial_expired || false}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Enterprise User"
                      name="is_enterprise"
                      checked={editedUser.is_enterprise || false}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
          <div className="d-flex gap-2 justify-content-end">
            <Button variant="secondary" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Update User
            </Button>

          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default UserEdit;